import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDateTimeBoth } from '../utils/dates';
import RequestAttachments from './RequestAttachments';

/**
 * خيط المراسلة بين الإدارة والعميل. يُستخدم في بوابة العميل (بلا clientId)
 * وفي صفحة العملاء للإدارة (مع clientId)، وعلى تذكرة بعينها (مع requestId).
 */
export default function ClientMessagesThread({ clientId, requestId, height = 380, onRead, readOnly, reloadKey }) {
  const { api, apiUpload, user } = useAuth();
  const toast = useToast();
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const qs = clientId ? `?client_id=${clientId}` : '';
  const listUrl = requestId ? `/client-requests/${requestId}/messages` : `/client-messages${qs}`;
  const sendUrl = requestId ? `/client-requests/${requestId}/reply` : `/client-messages${qs}`;

  const load = () => {
    api(listUrl)
      .then(d => { setMessages(d.messages || []); onRead?.(); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [clientId, requestId, reloadKey]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages.length]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const res = await api(sendUrl, { method: 'POST', body: JSON.stringify({ body: body.trim() }) });

      // مرفقات الرسالة تُربط بها بعد إنشائها ليعرف الطرف الآخر بأي ردّ ارتبطت
      if (requestId && files.length && res?.reply?.id) {
        for (const file of files) {
          const fd = new FormData();
          fd.append('files[]', file);
          fd.append('message_id', res.reply.id);
          try {
            await apiUpload(`/client-requests/${requestId}/attachments`, fd);
          } catch (err) { toast.error(`${file.name}: ${err.message}`); }
        }
      }

      setBody('');
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) { toast.error(err.message); }
    finally { setSending(false); }
  };

  const isMine = (m) => (user?.role === 'client' ? m.from_client : !m.from_client);

  if (loading) return <div className="loader">جاري التحميل...</div>;

  return (
    <div>
      <div style={{
        maxHeight: height, overflowY: 'auto', display: 'grid', gap: 10,
        padding: 4, background: 'var(--surface-alt)', borderRadius: 8,
      }}>
        {messages.length === 0 ? (
          <div className="empty" style={{ padding: 20 }}>لا توجد رسائل بعد</div>
        ) : messages.map(m => (
          <div key={m.id} style={{ display: 'flex', justifyContent: isMine(m) ? 'flex-start' : 'flex-end' }}>
            <div style={{
              maxWidth: '78%', padding: '9px 12px', borderRadius: 12,
              background: isMine(m) ? 'var(--primary-light)' : 'var(--surface)',
              border: '1px solid var(--border)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                {m.sender?.name || (m.from_client ? 'العميل' : 'فريق العمل')} · {fmtDateTimeBoth(m.created_at)}
                {isMine(m) && m.read_at && <span> · قُرئت</span>}
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{m.body}</div>
              {m.attachments?.length > 0 && (
                <div style={{ marginTop: 8 }}><RequestAttachments items={m.attachments} /></div>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {!readOnly && (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <textarea className="form-textarea" style={{ minHeight: 60, flex: 1 }}
              placeholder="اكتب رسالتك..." value={body} onChange={e => setBody(e.target.value)} />
            <button className="btn btn-primary" style={{ alignSelf: 'flex-end' }}
              onClick={send} disabled={sending || !body.trim()}>
              {sending ? 'جارٍ الإرسال...' : 'إرسال'}
            </button>
          </div>
          {requestId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <input ref={fileRef} type="file" multiple style={{ fontSize: 12 }}
                onChange={e => setFiles(Array.from(e.target.files || []))} />
              {files.length > 0 && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{files.length} ملف مع الرسالة</span>
              )}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>
            يصل إشعار بالبريد الإلكتروني للطرف الآخر عند كل رسالة.
          </div>
        </>
      )}
    </div>
  );
}
